import { useState, useRef, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Typography
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useLogin from 'hooks/useLogin';
import { useI18n } from 'i18n';

// assets
import { IconLogout, IconUserScan } from '@tabler/icons-react';

// ==============================|| PROFILE MENU ||============================== //

const GOLD = '#C9A84C';
const GOLD_DARK = '#8B6520';

const getInitial = (user) => {
  if (!user) return '?';
  const src = user.display_name || user.username || user.email || '?';
  return String(src).trim().charAt(0).toUpperCase() || '?';
};

// 头像图地址:'up'=上传图(接口,带 no-cache);aNN=预设;否则用首字母
const avatarUrl = (user) => {
  if (!user || !user.avatar) return undefined;
  return user.avatar === 'up' ? '/api/user/avatar' : `/avatars/${user.avatar}.svg`;
};

const ProfileSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const customization = useSelector((state) => state.customization);
  const account = useSelector((state) => state.account);
  const { logout } = useLogin();
  const { t } = useI18n();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const handleLogout = async () => {
    logout();
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const initial = getInitial(account.user);
  const displayName = account.user?.display_name || account.user?.username || t('profile.account');

  return (
    <>
      <Box
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        sx={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all .2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)'
          }
        }}
      >
        <Avatar
          src={avatarUrl(account.user)}
          sx={{
            width: 40,
            height: 40,
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
            color: '#fff',
            border: `2px solid ${GOLD}`,
            boxShadow: open
              ? `0 0 0 3px rgba(201, 168, 76, 0.25)`
              : `0 2px 8px rgba(201, 168, 76, 0.3)`,
            fontWeight: 700,
            fontSize: '1rem',
            letterSpacing: '0.5px',
            transition: 'all .2s ease-in-out'
          }}
        >
          {initial}
        </Avatar>
      </Box>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <Box sx={{ px: 2, pt: 2, pb: 1.5, minWidth: 200 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: GOLD_DARK }}>
                      {displayName}
                    </Typography>
                    {account.user?.email && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, wordBreak: 'break-all' }}>
                        {account.user.email}
                      </Typography>
                    )}
                  </Box>
                  <List
                    component="nav"
                    sx={{
                      width: '100%',
                      maxWidth: 350,
                      minWidth: 200,
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: '10px',
                      pt: 0,
                      [theme.breakpoints.down('md')]: {
                        minWidth: '100%'
                      },
                      '& .MuiListItemButton-root': {
                        mt: 0.5
                      }
                    }}
                  >
                    <ListItemButton sx={{ borderRadius: `${customization.borderRadius}px` }} onClick={() => { setOpen(false); navigate('/panel/profile'); }}>
                      <ListItemIcon>
                        <IconUserScan stroke={1.5} size="1.3rem" />
                      </ListItemIcon>
                      <ListItemText primary={<Typography variant="body2">{t('profile.center')}</Typography>} />
                    </ListItemButton>

                    <ListItemButton sx={{ borderRadius: `${customization.borderRadius}px` }} onClick={handleLogout}>
                      <ListItemIcon>
                        <IconLogout stroke={1.5} size="1.3rem" />
                      </ListItemIcon>
                      <ListItemText primary={<Typography variant="body2">{t('profile.logout')}</Typography>} />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
